import { forwardRef } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'accent-soft';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
}

// S-FX controls are pills (see DESIGN.md). Token roles work in both themes:
//  - primary:     the lit cyan CTA, one per view           (.sfx-btn-primary)
//  - secondary:   neutral control                          (bg-tertiary + hairline ring)
//  - accent-soft: inline "do this" action inside a list    (accent tint)
//  - ghost:       low-emphasis
//  - danger:      destructive action
const VARIANTS: Record<Variant, string> = {
  primary: 'sfx-btn-primary font-semibold',
  secondary:
    'bg-tertiary text-foreground ring-1 ring-inset ring-border hover:bg-tertiary/70 hover:ring-hairline-strong',
  'accent-soft': 'bg-accent/10 text-accent ring-1 ring-inset ring-accent/30 hover:bg-accent/[0.18]',
  ghost: 'text-muted-foreground hover:text-foreground hover:bg-tertiary/60',
  danger: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
};

const SIZES: Record<Size, string> = {
  sm: 'h-8 px-3 text-xs gap-1.5',
  md: 'h-9 px-4 text-sm gap-2',
  lg: 'h-11 px-5 text-sm gap-2',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'sm', loading, disabled, className, children, type = 'button', ...props },
  ref
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={cn(
        'inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-full font-medium',
        'transition-[background-color,box-shadow,color,transform] duration-fast ease-sfx active:scale-[0.97] active:duration-press',
        'disabled:pointer-events-none disabled:opacity-50',
        VARIANTS[variant],
        SIZES[size],
        className
      )}
      {...props}
    >
      {loading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
      {children}
    </button>
  );
});

interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Required: icon-only buttons need an accessible name. */
  label: string;
  tone?: 'default' | 'danger';
  size?: 'sm' | 'md';
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, tone = 'default', size = 'sm', className, children, type = 'button', ...props },
  ref
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full text-muted-foreground',
        'transition-[background-color,color,transform] duration-fast ease-sfx active:scale-[0.94] active:duration-press',
        'disabled:pointer-events-none disabled:opacity-50',
        tone === 'danger'
          ? 'hover:text-destructive hover:bg-destructive/10'
          : 'hover:text-foreground hover:bg-tertiary/70',
        size === 'sm' ? 'h-7 w-7' : 'h-9 w-9',
        className
      )}
      {...props}
    >
      {children}
    </button>
  );
});
